/**
 * @file RecomputeCostsCard.tsx
 * @brief Re-run the per-session cost calculation across a date range
 *        using the *current* `pricing_config` rows.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — GridBlock flush layout
 */

import { useState } from "react";
import { formatISO, subDays } from "date-fns";
import { LoaderCircle, Play, RotateCcw, RotateCcwClock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { GridBand, GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useRecomputeCosts } from "../api";
import { BILLING_SECTIONS } from "../billingNav";

export function RecomputeCostsCard() {
  const [from, setFrom] = useState<string>(
    formatISO(subDays(new Date(), 7), { representation: "date" }),
  );
  const [to, setTo] = useState<string>(formatISO(new Date(), { representation: "date" }));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastUpdatedCount, setLastUpdatedCount] = useState<number | null>(null);

  const recompute = useRecomputeCosts();

  const handleRun = async () => {
    setConfirmOpen(false);
    try {
      const res = await recompute.mutateAsync({
        startDate: from,
        endDate: to,
      });
      setLastUpdatedCount(res.updated);
      toast({
        variant: "success",
        title: "Recompute complete",
        description: `Updated ${res.updated} session(s).`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Recompute failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <>
      <GridBand id={BILLING_SECTIONS.recompute} className="bg-muted/25 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Recompute
        </p>
        <h3 className="mt-0.5 flex items-center gap-2 text-base font-semibold tracking-tight">
          <RotateCcwClock className="h-4 w-4 text-primary" />
          Recompute session costs
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Re-stamps cost rows for every session in the chosen window using the current service
          rates. Useful after editing OpenAI pricing above.
        </p>
      </GridBand>

      <GridCells className="grid-cols-1 sm:grid-cols-3">
        <GridCell className="sm:col-span-2 sm:border-r-0">
          {/* The app's bare field (DES 01-10-2026: "yahan bhi"). */}
          <div className="max-w-md">
            <FormFieldGroup label="Date range">
              <DateRangePicker
                from={from}
                to={to}
                onChange={({ from: f, to: t }) => {
                  if (f) setFrom(f);
                  if (t) setTo(t);
                }}
                size="sm"
                placeholder="Date range"
              />
            </FormFieldGroup>
          </div>
        </GridCell>
        <GridCell className="flex items-center sm:justify-end">
          <Button
            className="w-full sm:w-auto"
            onClick={() => setConfirmOpen(true)}
            disabled={!from || !to || recompute.isPending}
          >
            {recompute.isPending ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" /> Running…
              </>
            ) : (
              <>
                <Play className="h-4 w-4" /> Run recompute
              </>
            )}
          </Button>
        </GridCell>
      </GridCells>

      {lastUpdatedCount !== null ? (
        <GridCell>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <RotateCcw className="h-3.5 w-3.5" />
            Last run: updated{" "}
            <span className="font-semibold text-foreground">{lastUpdatedCount}</span> session(s).
          </div>
        </GridCell>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recompute session costs?</AlertDialogTitle>
            <AlertDialogDescription>
              This will overwrite cost values for every session whose `started_at` falls between{" "}
              <b>{from}</b> and <b>{to}</b> using the current OpenAI service rates. Existing invoice
              records are not modified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRun}>Run recompute</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
